import 'ag-grid-enterprise'
import { useQuery } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLocaleText } from '../../actions/datagrid'
import { calendarGetRange, mappingsQuery, payTypesQuery, personsGetAllQuery } from '../../api'
import { useGetCalendarSettings } from '../../api/endpoints/index.ts'
import { Button } from '../../common/Button.tsx'
import DefaultDatePicker from '../../common/datepicker/DefaultDatePicker.jsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../common/grid'
import LoadingCentered from '../../common/loaders/loadingcentered'
import { PdfReport } from '../../common/pdfreport'
import { dateFormatter, dateGetter } from '../../common/utils'
import { activeIntegrationsSelector, authSelector } from '../../reducers'
import { useGetAbscenceTypesOptions } from '../personalprofile/newabscencemodal/newabscencemodal.constants.js'
import { rowCount } from './calendarUtils.js'

const viewStateKey = 'calendarGridViewState'

export function CalendarGrid() {
	const { t } = useTranslation()
	const gridRef = useRef()
	const prefix = 'calendarGrid'

	const { companyName } = useSelector(authSelector)
	const { options: translatedOptions } = useGetAbscenceTypesOptions()
	const personsQuery = useQuery(personsGetAllQuery).data
	const persons = personsQuery?.data
	const { data: mappings } = useQuery(mappingsQuery)

	const activeIntegrations = useSelector(activeIntegrationsSelector)
	const isProcountorActive = !!activeIntegrations.tokenProcountor

	const { data: calendarSettings } = useGetCalendarSettings()
	const saturdaysCalculatedAsHoliday = calendarSettings?.saturdaysCalculatedAsHoliday !== false

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const salaryTypes = useQuery(payTypesQuery(isProcountorActive ? 'PROCOUNTOR' : 'ESALARY', 'ABSENCE')).data ?? [] // fetch pay type options based on active integration

	const mappingWithSalaryName = useMemo(() => {
		if (!mappings || !salaryTypes) return {}

		return mappings.reduce((acc, m) => {
			if (isProcountorActive && m.payrollSystemType !== 'PROCOUNTOR') return acc

			if (!isProcountorActive && m.payrollSystemType === 'PROCOUNTOR') return acc

			const salary = salaryTypes.find((s) => String(s.code) === String(m.salaryTypeCode))
			acc[m.absenceType] = {
				code: m.salaryTypeCode,
				name: salary?.name || salary?.description || '',
				payrollSystemType: m.payrollSystemType,
			}
			return acc
		}, {})
	}, [mappings, salaryTypes, isProcountorActive])

	const personsById = useMemo(() => {
		if (!persons) return {}
		return persons.reduce((acc, p) => {
			acc[String(p.personId)] = p
			return acc
		}, {})
	}, [persons])

	const eventLabelMap = translatedOptions.reduce((acc, category) => {
		category.options.forEach((option) => {
			acc[option.value] = option.label
		})
		return acc
	}, {})

	const [startDate, setStartDate] = useState(DateTime.now().startOf('month').toISODate())
	const [endDate, setEndDate] = useState(DateTime.now().endOf('month').toISODate())

	const handleStartDateChange = (date) => {
		setStartDate(date)
		if (date && endDate && date > endDate) setEndDate(null)
	}

	const handleEndDateChange = (date) => {
		setEndDate(date)
		if (date && startDate && date < startDate) setStartDate(null)
	}

	const isValidRange = !!(startDate && endDate && startDate <= endDate)
	const [events, setEvents] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const statusOptions = useMemo(
		() => [
			{ value: 'OPEN', label: t('Open', 'Avoinna') },
			{ value: 'APPROVED', label: t('Approved', 'Hyväksytty') },
			{ value: 'REJECTED', label: t('Rejected', 'Hylätty') },
			{ value: 'DONE', label: t('Done', 'Valmis') },
		],
		[t],
	)

	const getSalaryMapping = useCallback(
		(causeCode) => {
			if (!causeCode) return null

			let mapping = mappingWithSalaryName[causeCode]

			if (!mapping) {
				const salary = salaryTypes.find((s) => String(s.code) === String(causeCode))
				mapping = salary
					? {
							code: salary.code,
							name: salary.name || salary.description || '',
							payrollSystemType: isProcountorActive ? 'PROCOUNTOR' : 'ESALARY',
						}
					: null
			}

			if (isProcountorActive && mapping?.payrollSystemType !== 'PROCOUNTOR') {
				return null
			}

			return mapping
		},
		[mappingWithSalaryName, salaryTypes, isProcountorActive],
	)

	const fetchEvents = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const res = await calendarGetRange(startDate, endDate)
			if (res.status !== 200) throw new Error('Failed to load events')
			const filteredData = [
				...new Map(
					res.data.map((item) => [item.id, item]), // filter duplicates by id (events spanning multiple days)
				).values(),
			]
				.filter((e) => e.personId)
				.map((e) => {
					const person = personsById[String(e.personId)]
					const mapping = getSalaryMapping(e.causeCode)
					const start = e.startDate ? new Date(e.startDate) : ''
					const end = e.endDate ? new Date(e.endDate) : ''
					return {
						...e,
						personNr: person?.nr ?? '',
						payGroup: person?.controlDetails?.payGroup ?? '',
						salaryType: mapping?.code ? `${mapping.code} ${mapping.name}` : '',
						startDate: start,
						endDate: end,
					}
				})
			setEvents(filteredData)
		} catch (err) {
			setError(err)
		} finally {
			setLoading(false)
		}
	}, [getSalaryMapping, personsById, startDate, endDate])

	useEffect(() => {
		if (persons && isValidRange) fetchEvents()
	}, [persons, isValidRange, fetchEvents])

	const columnDefs = [
		{
			headerName: t('PersonNumber', 'Henkilönumero'),
			field: 'personNr',
			filter: true,
		},
		{
			headerName: t('Person', 'Henkilö'),
			field: 'personName',
		},
		{
			headerName: t('PayGroup', 'Maksuryhmä'),
			field: 'payGroup',
			filter: true,
		},
		{
			headerName: t('CauseCode', 'Syyn koodi'),
			field: 'causeCode',
			hide: true,
		},
		{
			headerName: t('Event', 'Tapahtuma'),
			field: 'event',
			valueGetter: (params) => eventLabelMap[params.data.causeCode] || params.data.causeCode,
		},
		{
			headerName: t('SalaryType', 'Palkkalaji'),
			field: 'salaryType',
		},
		{
			headerName: t('Status', 'Tila'),
			field: 'status',
			width: 125,
			filter: 'agSetColumnFilter',
			filterParams: {
				values: statusOptions.map((opt) => opt.value),
				valueFormatter: (params) => {
					const option = statusOptions.find((o) => o.value === params.value)
					return option ? option.label : params.value
				},
				suppressMiniFilter: false,
				applyMiniFilterWhileTyping: true,
			},
			valueFormatter: (params) => {
				const option = statusOptions.find((o) => o.value === params.value)
				return option ? option.label : params.value
			},
		},
		{
			headerName: t('Start date', 'Alkaa'),
			cellClass: 'dateType',
			field: 'startDate',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('End date', 'Päättyy'),
			cellClass: 'dateType',
			field: 'endDate',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('Count', 'Lukumäärä'),
			filter: 'agNumberColumnFilter',
			valueGetter: (params) => rowCount(params.data, saturdaysCalculatedAsHoliday),
			valueFormatter: (params) => params.value?.toFixed(2).replace('.', ',') || '',
		},
		{
			headerName: t('Info', 'Info'),
			field: 'info',
		},
	]

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => restoreViewState(viewStateKey, {}),
			})

			items.push('separator')

			items.push({
				name: t('ExportToPayroll', 'Vienti palkkaohjelmaan'),
				action: () => {
					const api = params.api
					if (!api) return

					const visibleRows = []
					api.forEachNodeAfterFilterAndSort((node) => {
						if (node.isSelected()) {
							visibleRows.push(node.data)
						}
					})
					if (!visibleRows.length) return alert(t('NoRowsSelected', 'Ei valittuja rivejä'))

					const csvRows = visibleRows.map((row) => {
						const start = row.startDate ? DateTime.fromJSDate(row.startDate).toFormat('dd.MM.yyyy') : ''

						const end = row.endDate ? DateTime.fromJSDate(row.endDate).toFormat('dd.MM.yyyy') : ''

						const mapping = getSalaryMapping(row.causeCode)

						const rowData = [
							'E',
							row.personNr || '',
							mapping?.code || '',
							mapping?.name || '',
							rowCount(row, saturdaysCalculatedAsHoliday).toFixed(2).replace('.', ','),
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							start,
							end,
							'',
						]
						return rowData.join(';')
					})

					const csvContent = `\uFEFF${csvRows.join('\n')}`
					const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.setAttribute('download', 'palkkaohjelma_export.csv')
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
				},
			})
			items.push('separator')

			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('Events', 'Tapahtumat'),
					companyname: companyName,
				}),
			)

			return items
		},
		[t, getSalaryMapping, companyName, saturdaysCalculatedAsHoliday],
	)

	const rowSelection = useMemo(() => {
		return {
			mode: 'multiRow',
			checkboxes: true,
			headerCheckbox: true,
		}
	}, [])

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
			{loading && <LoadingCentered show />}
			{error && <div className="text-danger text-center">{error.message}</div>}

			<Row className="mb-2 g-2 align-items-center">
				<Col xl={2} md="auto">
					<DefaultDatePicker
						id={`${prefix}.startDate`}
						name="startDate"
						label={t('Starting')}
						value={startDate}
						onChange={handleStartDateChange}
					/>
				</Col>
				–
				<Col xl={2} md="auto">
					<DefaultDatePicker
						id={`${prefix}.endDate`}
						name="endDate"
						label={t('Ending')}
						value={endDate}
						onChange={handleEndDateChange}
					/>
				</Col>
				<Col md="auto" className="align-self-end mb-2">
					{/* prevent invalid range api requests */}
					<Button
						variant="primary"
						style={{ width: '100px', height: '38px' }}
						onClick={isValidRange ? fetchEvents : undefined}
					>
						{t('Refresh', 'Hae')}
					</Button>
				</Col>
			</Row>

			<div className="ag-theme-material styles-ag flex-grow-1" style={{ height: 600 }}>
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					sideBar={sideBar}
					statusBar={statusBar}
					rowData={events}
					animateRows
					enableCellTextSelection
					rowSelection={rowSelection}
					suppressContextMenu={false}
					getContextMenuItems={getContextMenuItems}
					getRowId={(params) => String(params.data.id)}
					getLocaleText={(params) => getLocaleText(t, params)}
					initialState={loadViewState(viewStateKey)}
					onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
				/>
			</div>
		</>
	)
}
